import { useCallback, useEffect, useState } from "react";

import { may, send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { toneOf } from "../design/tone";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// SWEEP S14. WHAT THE CLIENT ASKED FOR, AND THE AGENCY'S ANSWER.
//
// The portal has let a client ask for a change since V24 and nothing in the console read
// it, so every request sat unanswered. Shown on the event it is about, newest open first.
// Answering is client.manage; reading is client.read. A request is answered once — the
// database refuses a second answer (`client_change_request_resolution_only`).
// ---------------------------------------------------------------------------

type ChangeRequest = {
  id: string;
  requirement_id: string | null;
  body: string;
  status: string;
  created_at: string;
  resolved_at: string | null;
  client_name: string;
  role_name: string | null;
  // S69 (0202). The state's words and tone, and the answers it offers, from the
  // database; this file holds no list of states and sends back only what it was offered.
  status_label: string;
  status_tone: string;
  moves: { status: string; label: string }[];
};

export function ChangeRequests({ eventId }: { eventId: string }) {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const mayAnswer = me ? may(me, "client.manage") : false;
  const [rows, setRows] = useState<ChangeRequest[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<{ change_requests?: ChangeRequest[] }>(console_.eventChangeRequests(apiBase, eventId));
      setRows(res.status === 200 ? (res.body.change_requests ?? []) : null);
    } catch {
      setRows(null);
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function answer(id: string, status: string) {
    setBusy(id);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(console_.resolveChangeRequest(apiBase, id, status));
      if (res.status !== 200) {
        setNotice(refusal(res.body.error) ?? t("That answer could not be recorded."));
        return;
      }
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(null);
    }
  }

  // Nothing to say and nobody asked: no panel at all, rather than an empty one on every event.
  if (rows === null || rows.length === 0) return null;
  const open = rows.filter((r) => r.moves.length > 0).length;

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("What the client asked for")}</h2>
        {open > 0 ? <StatusChip tone="attention" label={t("{n} waiting", { n: open })} /> : null}
      </div>
      {notice ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone="attention" label={t("Not recorded")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <ul className="plain-list">
        {rows.map((r) => (
          <li key={r.id} className="stack">
            <div className="row">
              <StatusChip tone={toneOf(r.status_tone)} label={translateLabel(locale, r.status_label)} />
              <span className="muted">
                {r.client_name}
                {r.role_name ? ` · ${translateLabel(locale, r.role_name)}` : ""} · {whenIn(r.created_at, orgZone)}
              </span>
            </div>
            <p>{r.body}</p>
            {r.moves.length > 0 && mayAnswer ? (
              <div className="row">
                {r.moves.map((m, i) => (
                  <button
                    key={m.status}
                    type="button"
                    className={i === 0 ? "btn" : "btn btn-quiet"}
                    disabled={busy !== null}
                    onClick={() => void answer(r.id, m.status)}
                  >
                    {busy === r.id && i === 0 ? t("Recording…") : translateLabel(locale, m.label)}
                  </button>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
